import { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, Bot, User, ArrowRight } from 'lucide-react';
import { api } from '../../lib/api';
import { useLocation } from 'wouter';
import { Button } from '../ui/button';
import { useAuthStore } from '../../hooks/useAuth';
import { isOperationsTierUser } from '../../utils/entitlements';
import { OperationsFeatureGate } from '../entitlements/OperationsFeatureGate';

interface Message {
    role: 'user' | 'assistant';
    content: string;
    suggestedActions?: { label: string; href: string }[];
}


export function BayWhizCopilot({ warehouseId }: { warehouseId?: string }) {
    const { user } = useAuthStore();
    const isOperationsEntitled = isOperationsTierUser(user);
    const [, setLocation] = useLocation();
    const [messages, setMessages] = useState<Message[]>([
        {
            role: 'assistant',
            content: "Hi! I'm BayWhiz Copilot. Ask me anything about your operations, risk trends, or carriers.",
        },
    ]);
    const [input, setInput] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isThinking]);

    if (!isOperationsEntitled) {
        return (
            <OperationsFeatureGate
                featureName="BayWhiz Copilot"
                description="BayWhiz Copilot provides rule-based operational Q&A, what-if delay simulations, carrier reliability lookups, and risk-queue triage on the Operations ($199 USD/month) plan and active 30-day trials."
                benefits={[
                    'Interactive operational queries across appointments, carriers, and risk alerts',
                    'On-demand what-if delay and dock cascade simulations',
                    'Quick acknowledgement and snoozing of high-risk arrivals',
                ]}
                testId="copilot-operations-gate"
            />
        );
    }

    const handleSend = async () => {
        const text = input.trim();
        if (!text || isThinking) return;

        setMessages((prev) => [...prev, { role: 'user', content: text }]);
        setInput('');
        setIsThinking(true);

        try {
            const res = await api.copilotAsk(text, warehouseId);
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: res.answer ?? 'No response.',
                    suggestedActions: res.suggestedActions,
                },
            ]);
        } catch (err: any) {
            setMessages((prev) => [
                ...prev,
                { role: 'assistant', content: `Sorry, something went wrong. ${err?.message || 'Please try again.'}` },
            ]);
        } finally {
            setIsThinking(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm flex flex-col" style={{ minHeight: '420px', maxHeight: '560px' }}>
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center gap-2 bg-gradient-to-r from-indigo-500/10 to-purple-500/5 shrink-0">
                <div className="p-1.5 bg-indigo-500/15 rounded-lg border border-indigo-500/20">
                    <Sparkles className="w-4 h-4 text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-semibold text-foreground leading-tight">Ask BayWhiz</h2>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-medium">AI Copilot</p>
                </div>

            </div>

            {/* Message area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, idx) => (
                    <div
                        key={idx}
                        className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                        {/* Avatar */}
                        <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white ${msg.role === 'assistant' ? 'bg-indigo-600' : 'bg-slate-600'}`}>
                            {msg.role === 'assistant'
                                ? <Bot className="w-3.5 h-3.5" />
                                : <User className="w-3.5 h-3.5" />
                            }
                        </div>
                        {/* Bubble */}
                        <div
                            className={`max-w-[80%] text-sm rounded-xl px-3.5 py-2.5 leading-relaxed whitespace-pre-wrap ${msg.role === 'assistant'
                                ? 'bg-muted text-foreground rounded-tl-none'
                                : 'bg-indigo-600 text-white rounded-tr-none'
                                }`}
                        >
                            {msg.content}
                            {msg.suggestedActions && msg.suggestedActions.length > 0 && (
                                <div className="mt-2 flex flex-col gap-1">
                                    {msg.suggestedActions.map((action, aIdx) => (
                                        <button
                                            key={aIdx}
                                            onClick={() => setLocation(action.href)}
                                            className="flex items-center gap-1 text-[11px] text-indigo-400 hover:text-indigo-300 hover:underline text-left"
                                        >
                                            <ArrowRight className="w-3 h-3 shrink-0" />
                                            {action.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {/* Thinking indicator */}
                {isThinking && (
                    <div className="flex gap-2.5 flex-row">
                        <div className="shrink-0 w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center">
                            <Bot className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="bg-muted rounded-xl rounded-tl-none px-4 py-3 flex gap-1 items-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                    </div>
                )}
                <div ref={bottomRef} />
            </div>

            {/* Input area */}
            <div className="p-3 border-t border-border shrink-0 flex gap-2 items-end bg-background/50">
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about delay trends, carriers, or workload…"
                    rows={2}
                    className="flex-1 resize-none rounded-lg border border-border bg-background text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 placeholder:text-muted-foreground/60 text-foreground"
                />
                <Button
                    size="icon"
                    onClick={handleSend}
                    disabled={!input.trim() || isThinking}
                    className="h-9 w-9 shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-40"
                >
                    <Send className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
}
